import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { PageCard } from '../layout/page/PageCard';
import { DeleteIconButton } from '../ui/DeleteIconButton';
import { InlineAlert } from '../ui/InlineAlert';
import { RoundedCheckbox } from '../ui/RoundedCheckbox';
import { usePermissions } from '../../contexts/PermissionsContext';
import { canWriteProductCatalog } from './productCatalogAccess';
import {
  MarketSegmentPriority,
  SalesProductMarketSegmentLink,
  SalesProductMarketSegmentLinkItemPayload,
  getProductMarketSegments,
  listMarketSegments,
  replaceProductMarketSegments,
} from '../../services/marketSegmentsApi';
import { CatalogMultiSelectModal, CatalogSelectItem } from './CatalogMultiSelectModal';

type ProductMarketSegmentsPanelProps = {
  productId: string;
  readOnly?: boolean;
};

type DraftLink = SalesProductMarketSegmentLinkItemPayload & {
  name: string;
};

const PRIORITY_OPTIONS: { value: MarketSegmentPriority; label: string }[] = [
  { value: 'Primary', label: 'Primário' },
  { value: 'Secondary', label: 'Secundário' },
  { value: 'Experimental', label: 'Experimental' },
];

export function ProductMarketSegmentsPanel({
  productId,
  readOnly = false,
}: ProductMarketSegmentsPanelProps) {
  const { canWriteModule } = usePermissions();
  const canWrite = !readOnly && canWriteProductCatalog(canWriteModule);
  const [links, setLinks] = useState<DraftLink[]>([]);
  const [checkedIds, setCheckedIds] = useState<Set<string>>(() => new Set());
  const [modalOpen, setModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const linkedIds = useMemo(() => links.map((link) => link.marketSegmentId), [links]);

  useEffect(() => {
    void (async () => {
      setLoading(true);
      try {
        const productLinks = await getProductMarketSegments(productId);
        setLinks(mapLinksToDraft(productLinks));
        setCheckedIds(new Set());
      } catch {
        toast.error('Falha ao carregar segmentos do produto.');
      } finally {
        setLoading(false);
      }
    })();
  }, [productId]);

  function mapLinksToDraft(productLinks: SalesProductMarketSegmentLink[]): DraftLink[] {
    return productLinks.map((link) => ({
      marketSegmentId: link.marketSegmentId,
      name: link.name,
      priority: link.priority,
      sortOrder: link.sortOrder,
    }));
  }

  function updateLink(marketSegmentId: string, patch: Partial<Pick<DraftLink, 'priority'>>): void {
    if (!canWrite) return;
    setLinks((current) =>
      current.map((link) => (link.marketSegmentId === marketSegmentId ? { ...link, ...patch } : link)),
    );
  }

  function toggleChecked(id: string): void {
    setCheckedIds((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function removeOne(id: string): void {
    if (!canWrite) return;
    setLinks((current) => current.filter((link) => link.marketSegmentId !== id));
    setCheckedIds((current) => {
      const next = new Set(current);
      next.delete(id);
      return next;
    });
  }

  function removeSelected(): void {
    if (!canWrite || checkedIds.size === 0) return;
    setLinks((current) => current.filter((link) => !checkedIds.has(link.marketSegmentId)));
    setCheckedIds(new Set());
  }

  function handleModalConfirm(selected: CatalogSelectItem[]): void {
    if (!canWrite || selected.length === 0) return;
    setLinks((current) => {
      const existing = new Set(current.map((link) => link.marketSegmentId));
      const additions = selected
        .filter((item) => !existing.has(item.id))
        .map((item, index) => ({
          marketSegmentId: item.id,
          name: item.title,
          priority: 'Primary' as MarketSegmentPriority,
          sortOrder: current.length + index,
        }));
      return [...current, ...additions];
    });
  }

  async function handleSave(): Promise<void> {
    if (!canWrite) return;
    setSaving(true);
    try {
      const items: SalesProductMarketSegmentLinkItemPayload[] = links.map((link, index) => ({
        marketSegmentId: link.marketSegmentId,
        priority: link.priority ?? 'Primary',
        sortOrder: index,
      }));
      await replaceProductMarketSegments(productId, items);
      toast.success('Segmentos do produto atualizados.');
    } catch {
      toast.error('Falha ao salvar segmentos do produto.');
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return <p className="text-secondary-500">Carregando segmentos...</p>;
  }

  return (
    <PageCard
      title="Segmentos de mercado"
      subtitle="Vincule segmentos do catálogo e defina a prioridade estratégica."
    >
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-secondary-600">
          Catálogo em{' '}
          <Link to="/sales/market-segments" className="erp-link">
            Segmentos de mercado
          </Link>
          .
        </p>
        {canWrite ? (
          <div className="erp-btn-actions">
            <button
              type="button"
              className="erp-btn-danger erp-btn-sm"
              disabled={checkedIds.size === 0}
              onClick={removeSelected}
            >
              Excluir selecionados
            </button>
            <button type="button" className="erp-btn-secondary" onClick={() => setModalOpen(true)}>
              Adicionar segmentos
            </button>
          </div>
        ) : null}
      </div>

      {links.length === 0 ? (
        <InlineAlert>Nenhum segmento vinculado. Use “Adicionar segmentos”.</InlineAlert>
      ) : (
        <ul className="space-y-2">
          {links.map((link) => (
            <li key={link.marketSegmentId} className="rounded-lg border border-secondary-200 px-3 py-2">
              <div className="flex items-start gap-3">
                {canWrite ? (
                  <RoundedCheckbox
                    className="mt-1"
                    checked={checkedIds.has(link.marketSegmentId)}
                    onChange={() => toggleChecked(link.marketSegmentId)}
                  />
                ) : null}
                <div className="min-w-0 flex-1">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <span className="font-medium text-secondary-900">{link.name}</span>
                    </div>
                    {canWrite ? (
                      <DeleteIconButton
                        label="Excluir"
                        onClick={() => removeOne(link.marketSegmentId)}
                      />
                    ) : null}
                  </div>
                  <div className="mt-2">
                    <label className="erp-label-inline text-sm">
                      Prioridade
                      <select
                        className="erp-input ml-2"
                        disabled={!canWrite}
                        value={link.priority ?? 'Primary'}
                        onChange={(event) =>
                          updateLink(link.marketSegmentId, {
                            priority: event.target.value as MarketSegmentPriority,
                          })
                        }
                      >
                        {PRIORITY_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </label>
                  </div>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}

      {canWrite ? (
        <div className="mt-4">
          <button
            type="button"
            className="erp-btn-primary"
            disabled={saving}
            onClick={() => void handleSave()}
          >
            {saving ? 'Salvando...' : 'Salvar segmentos'}
          </button>
        </div>
      ) : null}

      <CatalogMultiSelectModal
        open={modalOpen}
        title="Selecionar segmentos"
        subtitle="Role para carregar mais itens do catálogo."
        alreadySelectedIds={linkedIds}
        fetchPage={async (page, pageSize, search) => {
          const result = await listMarketSegments({ activeOnly: true, page, pageSize, search });
          return {
            items: result.items.map((item) => ({
              id: item.id,
              title: item.name,
              subtitle: item.segmentType,
              description: item.description,
            })),
            totalCount: result.totalCount,
            page: result.page,
            pageSize: result.pageSize,
          };
        }}
        onClose={() => setModalOpen(false)}
        onConfirm={handleModalConfirm}
      />
    </PageCard>
  );
}
